import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "src/store/features/auth/axiosCSRF";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import { getApiErrorMessage } from "src/types/api";

export interface iAiChatActiveResponse {
  active_status: "ON" | "OFF" | boolean | string;
  limit_time: string | null;
  message: string;
}

export interface iAiChatActivateParams {
  active_status: boolean;
  days?: number;
}

// Ai Chat DeleteState 추가
export interface iAiChatDeleteState{
  type_hint : string;
  library_id : string;
  delete_all : boolean;
  round_num : number;
  created_at?: string;
}

export interface iAiChatActiveCheckState {
  response: iAiChatActiveResponse | null;
  activateResponse: iAiChatActiveResponse | null;
  deleteResponse : iAiChatDeleteState | null;
  isDeleteLoading : boolean 
  isLoading: boolean;
  isActivateLoading: boolean;
  message: string | null;
}

const initialState: iAiChatActiveCheckState = {
  response: null,
  activateResponse: null,
  deleteResponse : null,
  isDeleteLoading : false,
  isLoading: false,
  isActivateLoading: false,
  message: "",
};

export const aiChatActiveCheckAPI = createAsyncThunk(
  "aiChatActiveCheckAPI",
  async (_, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = "/review/ai-chat-activate-check/";
      const response = await axiosInstance.get(`${djangoEndpoint}${pathParam}`, {
        withCredentials: true,
      });

      return response.data;
    } catch (error) {
      const errorMessage = getApiErrorMessage(error);

      return rejectWithValue(errorMessage);
    }
  }
);

export const aiChatDeleteAPI = createAsyncThunk(
 "aiChatDeleteAPI",
 async (data : iAiChatDeleteState, {rejectWithValue}) => {
   try{
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = "/review/ai-chat/delete/";
      const requestData: iAiChatDeleteState = {
        type_hint: data.type_hint,
        library_id : data.library_id,
        delete_all : data.delete_all,
        round_num : data.round_num,
      };
      if (data.created_at) requestData.created_at = data.created_at;
      const response = await axiosInstance.post(`${djangoEndpoint}${pathParam}`,
        requestData,
        {
          withCredentials: true,
        }
      )

      return response.data;

   } catch (error) {
      const errorMessage = getApiErrorMessage(error);
      return rejectWithValue(errorMessage);
   }
 }
)
export const aiChatActivateAPI = createAsyncThunk(
  "aiChatActivateAPI",
  async (data: iAiChatActivateParams, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = "/review/ai-chat-activate/";
      const requestData: iAiChatActivateParams = {
        active_status: data.active_status,
      };

      if (data.days !== undefined) {
        requestData.days = data.days;
      }

      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        requestData,
        { withCredentials: true }
      );

      return response.data;
    } catch (error) {
      const errorMessage = getApiErrorMessage(error);

      return rejectWithValue(errorMessage);
    }
  }
);

export const aiChatActiveCheckSlice = createSlice({
  name: "aiChatActiveCheck",
  initialState,
  reducers: {
    resetAiChatActiveCheck: (state) => {
      state.response = null;
      state.activateResponse = null;
      state.deleteResponse = null;
      state.isDeleteLoading = false;
      state.isLoading = false;
      state.isActivateLoading = false;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(aiChatActiveCheckAPI.pending, (state) => {
        state.isLoading = true;
        state.message = "";
      })
      .addCase(aiChatActiveCheckAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.response = action.payload;
      })
      .addCase(aiChatActiveCheckAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.message = action.payload as string;
      })
      .addCase(aiChatActivateAPI.pending, (state) => {
        state.isActivateLoading = true;
        state.message = "";
      })
      .addCase(aiChatActivateAPI.fulfilled, (state, action) => {
        state.isActivateLoading = false;
        state.activateResponse = action.payload;
        state.response = action.payload;
      })
      .addCase(aiChatActivateAPI.rejected, (state, action) => {
        state.isActivateLoading = false;
        state.message = action.payload as string;
      })
      .addCase(aiChatDeleteAPI.pending, (state, action)=>{
        state.isDeleteLoading = true;
        state.message = ""
      })
      .addCase(aiChatDeleteAPI.fulfilled,(state,action)=>{
        state.isDeleteLoading = false;
        state.deleteResponse = action.payload;
      })
      .addCase(aiChatDeleteAPI.rejected,(state,action)=>{
        state.isDeleteLoading = false;
        state.message = action.payload as string;
      })
  },
});

export const { resetAiChatActiveCheck } = aiChatActiveCheckSlice.actions;
export default aiChatActiveCheckSlice.reducer;
